import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronLeft, ChevronRight, ShoppingCart, Filter, Trash2, BarChart3 } from 'lucide-react';
import { getAdminRevenue, getAdminOrders, deleteAdminOrder } from '../../services/api';
import { toast } from 'sonner';

const fmt = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);
const ORDERS_PER_PAGE = 20;

export const AdminOrdersPanel = () => {
  const [revenue, setRevenue] = useState(null);
  const [orders, setOrders] = useState([]);
  const [revenuePeriod, setRevenuePeriod] = useState('monthly');
  const [orderFilter, setOrderFilter] = useState({ status: '', order_type: '', clinic_id: '' });
  const [orderPage, setOrderPage] = useState(1);
  const [deleting, setDeleting] = useState(null);

  const loadOrders = useCallback(async () => {
    try { const res = await getAdminOrders(orderFilter); setOrders(res.data); } catch (e) { console.error(e); }
  }, [orderFilter]);

  const loadRevenue = useCallback(async () => {
    try { const res = await getAdminRevenue(revenuePeriod); setRevenue(res.data); } catch (e) { console.error(e); }
  }, [revenuePeriod]);

  useEffect(() => { loadOrders(); loadRevenue(); }, [loadOrders, loadRevenue]);

  const handleDelete = async (orderId) => {
    if (!window.confirm('Bu siparişi silmek istediğinize emin misiniz?')) return;
    setDeleting(orderId);
    try {
      await deleteAdminOrder(orderId);
      toast.success('Sipariş silindi');
      loadOrders();
      loadRevenue();
    } catch (e) {
      toast.error('Sipariş silinemedi');
    } finally {
      setDeleting(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(orders.length / ORDERS_PER_PAGE));
  const paged = orders.slice((orderPage - 1) * ORDERS_PER_PAGE, orderPage * ORDERS_PER_PAGE);

  return (
    <div className="space-y-4">
      {/* Revenue Summary */}
      {revenue && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-[#8E8E93] mb-1">Toplam Gelir</p>
            <p className="text-xl font-bold text-gray-900">{fmt(revenue.total_revenue)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-[#8E8E93] mb-1">Abonelik</p>
            <p className="text-xl font-bold text-[#6b6de3]">{fmt(revenue.subscription_revenue)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-[#8E8E93] mb-1">SMS</p>
            <p className="text-xl font-bold text-[#34C759]">{fmt(revenue.sms_revenue)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-[#8E8E93] mb-1">Sipariş</p>
            <p className="text-xl font-bold text-gray-900">{revenue.order_count}</p>
          </div>
        </div>
      )}

      {/* Revenue Chart */}
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Gelir Grafiği</h3>
          <Select value={revenuePeriod} onValueChange={setRevenuePeriod}>
            <SelectTrigger className="w-28 h-8 text-xs border-0 bg-[#F2F2F7] rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Günlük</SelectItem>
              <SelectItem value="weekly">Haftalık</SelectItem>
              <SelectItem value="monthly">Aylık</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {revenue?.chart_data?.length > 0 ? (
          <div className="overflow-x-auto">
            <div className="flex items-end gap-1" style={{ height: '120px' }}>
              {revenue.chart_data.map((d, i) => {
                const max = Math.max(...revenue.chart_data.map(x => x.amount));
                const pct = max > 0 ? Math.max((d.amount / max) * 100, 6) : 6;
                return (
                  <div key={i} className="flex flex-col items-center flex-shrink-0 flex-1 min-w-[28px] max-w-[60px] justify-end h-full" title={`${d.date}: ${fmt(d.amount)}`}>
                    <span className="text-[8px] text-gray-500 font-medium mb-1">{fmt(d.amount)}</span>
                    <div className="w-full max-w-[32px] bg-[#6b6de3] rounded-t-md hover:bg-[#5558d4] transition-colors cursor-default" style={{ height: `${pct}%`, minHeight: '4px' }} />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-1 mt-1.5 border-t border-gray-100 pt-1.5">
              {revenue.chart_data.map((d, i) => (
                <div key={i} className="flex-shrink-0 flex-1 min-w-[28px] max-w-[60px] text-center">
                  <span className="text-[9px] text-[#8E8E93]">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-[#C7C7CC]">
            <BarChart3 size={32} className="mb-2" />
            <p className="text-xs text-[#8E8E93]">Henüz gelir verisi yok</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-3 shadow-sm flex flex-wrap gap-2 items-center">
        <Filter size={14} className="text-[#8E8E93]" />
        <Select value={orderFilter.status || 'all'} onValueChange={v => setOrderFilter(p => ({ ...p, status: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-28 h-8 text-xs border-0 bg-[#F2F2F7] rounded-lg"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="completed">Başarılı</SelectItem>
            <SelectItem value="pending">Bekliyor</SelectItem>
            <SelectItem value="failed">Başarısız</SelectItem>
          </SelectContent>
        </Select>
        <Select value={orderFilter.order_type || 'all'} onValueChange={v => setOrderFilter(p => ({ ...p, order_type: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-28 h-8 text-xs border-0 bg-[#F2F2F7] rounded-lg"><SelectValue placeholder="Tip" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="subscription">Abonelik</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" onClick={() => { setOrderPage(1); loadOrders(); }} className="text-xs h-8">Filtrele</Button>
      </div>

      {/* Orders List */}
      <div className="space-y-2">
        {paged.length > 0 ? paged.map(o => (
          <div key={o.id} className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`order-${o.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-semibold text-sm text-gray-900">{o.clinic_name}</p>
                <p className="text-xs text-[#8E8E93]">{o.user_name} &bull; {new Date(o.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${o.status === 'completed' ? 'bg-green-100 text-green-600' : o.status === 'pending' ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'}`}>
                  {o.status === 'completed' ? 'Başarılı' : o.status === 'pending' ? 'Bekliyor' : 'Başarısız'}
                </span>
                <button
                  onClick={() => handleDelete(o.id)}
                  disabled={deleting === o.id}
                  className="p-1.5 rounded-lg text-[#C7C7CC] hover:text-[#FF3B30] hover:bg-red-50 transition-all disabled:opacity-50"
                  title="Siparişi Sil"
                  data-testid={`delete-order-${o.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${o.type === 'subscription' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                  {o.type === 'subscription' ? 'Abonelik' : 'SMS'}
                </span>
                <span className="text-xs text-[#8E8E93]">{o.plan_name || o.package_name}</span>
              </div>
              <span className="text-base font-bold text-gray-900">{fmt(o.amount)}</span>
            </div>
          </div>
        )) : (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <ShoppingCart size={40} className="text-[#C7C7CC] mx-auto mb-2" />
            <p className="text-[#8E8E93] text-sm">Sipariş bulunamadı</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {orders.length > ORDERS_PER_PAGE && (
        <div className="flex items-center justify-between mt-4" data-testid="orders-pagination">
          <p className="text-xs text-[#8E8E93]">{orders.length} sipariş, sayfa {orderPage}/{totalPages}</p>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={orderPage <= 1} onClick={() => setOrderPage(p => p - 1)} className="h-8 w-8 p-0 rounded-lg text-xs">
              <ChevronLeft size={16} />
            </Button>
            <Button size="sm" variant="outline" disabled={orderPage >= totalPages} onClick={() => setOrderPage(p => p + 1)} className="h-8 w-8 p-0 rounded-lg text-xs">
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
